// PR-E27: Add-to-calendar provider menu.
//
// Replaces the single "Add to Calendar" button (which generated an .ics and
// opened the iOS Share sheet, perceived as doing nothing when cancelled) with a
// chooser offering five providers:
//   - Apple   -> the existing .ics path (Filesystem write + Share on native,
//                blob download on web). Passed in as onApple so the event-object
//                sourcing stays identical to today.
//   - Google / Outlook (personal) / Office365 (work) / Yahoo -> external web
//     deep links opened via window.open(url, "_system"), which Capacitor routes
//     to the system browser/app instead of the in-app WebView.
//
// Provider glyphs are tiny inline SVGs so we add no icon dependency.

import { CalendarPlus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  buildCalendarLinks,
  type CalendarLinkEvent,
} from "@/lib/calendarLinks";

// Opens an external URL outside the in-app WebView. On Capacitor the "_system"
// target hands the URL to the OS (Safari / the provider app); on plain web it
// opens a new tab. noopener keeps the opener reference out of the new context.
export function openExternal(url: string): void {
  if (typeof window === "undefined") return;
  window.open(url, "_system", "noopener");
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
      <path d="M16.36 12.86c-.02-2.07 1.69-3.06 1.77-3.11-.96-1.41-2.46-1.6-3-1.62-1.27-.13-2.49.75-3.14.75-.65 0-1.65-.73-2.71-.71-1.39.02-2.68.81-3.4 2.06-1.45 2.52-.37 6.25 1.04 8.29.69.99 1.51 2.1 2.58 2.06 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.71.65 1.12-.02 1.83-1.01 2.51-2.01.79-1.15 1.12-2.27 1.13-2.33-.03-.01-2.17-.83-2.19-3.31zM14.3 6.55c.57-.69.96-1.65.85-2.61-.82.03-1.82.55-2.41 1.24-.53.61-.99 1.59-.87 2.52.92.07 1.85-.47 2.43-1.15z" />
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
      <path fill="#4285F4" d="M21.6 12.2c0-.64-.06-1.25-.16-1.84H12v3.49h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.99-4.3 2.99-7.17z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.5c-.9.6-2.04.96-3.39.96-2.6 0-4.8-1.76-5.59-4.12H3.07v2.58A10 10 0 0 0 12 22z" />
      <path fill="#FBBC05" d="M6.41 13.91a6 6 0 0 1 0-3.82V7.51H3.07a10 10 0 0 0 0 8.98l3.34-2.58z" />
      <path fill="#EA4335" d="M12 5.95c1.47 0 2.78.5 3.81 1.49l2.86-2.86A10 10 0 0 0 12 2a10 10 0 0 0-8.93 5.51l3.34 2.58C7.2 7.71 9.4 5.95 12 5.95z" />
    </svg>
  );
}

function OutlookGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0078D4" aria-hidden>
      <path d="M13 4h7a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-7v-3h6V7h-6V4z" />
      <path d="M2 6.2 11 4.5v15L2 17.8V6.2zm4.5 3.1c-1.3 0-2.2 1.2-2.2 2.7s.9 2.7 2.2 2.7 2.2-1.2 2.2-2.7-.9-2.7-2.2-2.7zm0 1.3c.6 0 1 .6 1 1.4s-.4 1.4-1 1.4-1-.6-1-1.4.4-1.4 1-1.4z" />
    </svg>
  );
}

function Office365Glyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#EB3C00" aria-hidden>
      <path d="M3 18.2 13 22V2L6.5 4.4 3 6v12.2zm10-.7-7-2.5V8.9l7-2.5v11.1z" opacity="0.85" />
      <path d="M14 3.2 19.5 1.5a1 1 0 0 1 1.3 1V21.5a1 1 0 0 1-1.3 1L14 20.8V3.2z" />
    </svg>
  );
}

function YahooGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#6001D2" aria-hidden>
      <path d="M3 6h3.7l2.6 4.7L11.9 6h3.6l-4.6 8v4H8.6v-4L3 6z" />
      <circle cx="17.5" cy="16.5" r="1.7" />
      <path d="M15.8 6h3.3l-1.6 7.2h-2.4L15.8 6z" />
    </svg>
  );
}

export function AddToCalendarMenu({
  event,
  onApple,
  className,
  style,
  testId,
  label = "Add to Calendar",
}: {
  // Same shape the .ics path already consumes; onApple receives nothing because
  // the caller closes over the exact event object it builds today.
  event: CalendarLinkEvent;
  onApple: () => void;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  label?: string;
}) {
  const links = buildCalendarLinks(event);
  const providers: {
    key: keyof typeof links | "apple";
    name: string;
    glyph: React.ReactNode;
    onSelect: () => void;
    testId: string;
  }[] = [
    { key: "apple", name: "Apple", glyph: <AppleGlyph />, onSelect: onApple, testId: "addtocal-provider-apple" },
    { key: "google", name: "Google", glyph: <GoogleGlyph />, onSelect: () => openExternal(links.google), testId: "addtocal-provider-google" },
    { key: "outlook", name: "Outlook", glyph: <OutlookGlyph />, onSelect: () => openExternal(links.outlook), testId: "addtocal-provider-outlook" },
    { key: "office365", name: "Office365", glyph: <Office365Glyph />, onSelect: () => openExternal(links.office365), testId: "addtocal-provider-office365" },
    { key: "yahoo", name: "Yahoo", glyph: <YahooGlyph />, onSelect: () => openExternal(links.yahoo), testId: "addtocal-provider-yahoo" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={className}
          style={style}
        >
          <CalendarPlus className="w-3.5 h-3.5" /> {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[11rem]">
        <DropdownMenuLabel className="text-[11px] text-muted-foreground">
          Add to
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {providers.map((p) => (
          <DropdownMenuItem
            key={p.key}
            data-testid={p.testId}
            onSelect={p.onSelect}
            className="gap-2 cursor-pointer"
          >
            <span className="inline-flex items-center justify-center w-5 h-5 shrink-0">
              {p.glyph}
            </span>
            <span className="text-sm">{p.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
